import { useCallback, useMemo, useState } from 'react'
import { useLazyQuery } from '@apollo/client'

import { GET_TASK_TEMPLATES } from 'src/graphql/taskTemplates.graphql'
import { toast } from 'src/components/Overlays/Toast/Toast'

import { FilterOption } from '../types'

interface UseTaskTemplatesFilterProps {
  selectedTaskTemplates: string[]
  onTaskTemplatesChange: (templateIds: string[]) => void
}

export const useTaskTemplatesFilter = ({
  selectedTaskTemplates,
  onTaskTemplatesChange,
}: UseTaskTemplatesFilterProps) => {
  const [options, setOptions] = useState<FilterOption[]>([])
  const [isLoaded, setIsLoaded] = useState(false)

  const onError = useCallback(() => {
    toast('Failed to load task templates', 'error')
  }, [])

  const [loadTemplates, { loading }] = useLazyQuery(GET_TASK_TEMPLATES, {
    variables: {
      pageInfo: { offset: 0, limit: 200 },
      orderBy: { field: 'createdAt', direction: 'desc' },
    },
    onCompleted: (data) => {
      const items = data?.taskTemplates?.items || []
      const opts: FilterOption[] = items
        .filter((t) => t?.taskTemplateId && t?.title)
        .map((t) => ({
          value: t.taskTemplateId,
          label: t.title,
        }))
      setOptions(opts)
      setIsLoaded(true)
    },
    onError,
  })

  const optionsWithSelection = useMemo(() => {
    return options.map((opt) => ({
      ...opt,
      selected: selectedTaskTemplates.includes(opt.value),
    }))
  }, [options, selectedTaskTemplates])

  const loadData = useCallback(() => {
    if (!isLoaded && !loading) {
      loadTemplates()
    }
  }, [isLoaded, loading, loadTemplates])

  return {
    options: optionsWithSelection,
    selected: selectedTaskTemplates,
    onChange: onTaskTemplatesChange,
    isLoaded,
    loadData,
    loading,
  }
}

